import "./FileDialog.scss";

import data from "./data";
import icons from "./icons";
import FileInfo from "./FileInfo";
import formatBytes from "./helpers/formatBytes";
import { splitUploadItemsByLimit } from "./helpers/imageOptimization.mjs";

let nextDialogId = 0;

const splitItems = ( items, callback ) => {
    const pass = [];
    const fail = [];
    ( Array.isArray( items ) ? items : [] ).forEach( ( item, index ) => {
        if ( callback( item, index ) ) pass.push( item );
        else fail.push( item );
    } );
    return [ pass, fail ];
};

/**
 * A dialog to display and capture information for files being uploaded.
 */
export default class FileDialog {
    /**
     * Create a new instance of the class.
     * @param {Form} parent The parent `Form` for this instance.
     */
    constructor( parent ) {
        this.onCancelOrCloseClicked = this.onCancelOrCloseClicked.bind( this );
        this.onTrapFocusKeyDown = this.onTrapFocusKeyDown.bind( this );
        this.onUploadClicked = this.onUploadClicked.bind( this );

        this.parentForm = parent;
        this.element = this.createElement();
        this.body = this.element.querySelector( ".fg-feu-dialog-body" );
        this.loader = null;
        this.preparationMessage = "";
        this.preparationTotal = 0;
        this.isPreparing = false;
        this.buttonClose  = this.element.querySelector( ".fg-feu-dialog-close" );
        this.buttonClose?.addEventListener( "click", this.onCancelOrCloseClicked );
        this.buttonCancel  = this.element.querySelector( ".fg-feu-dialog-cancel" );
        this.buttonCancel?.addEventListener( "click", this.onCancelOrCloseClicked );
        this.buttonUpload  = this.element.querySelector( ".fg-feu-dialog-upload" );
        if ( typeof data.rest.endpoint === "string" && typeof data.rest.nonce === "string" ) {
            this.buttonUpload?.addEventListener( "click", this.onUploadClicked );
        }
        this.fileInfos = new Map();
        this._firstOnlyValues = new Map();
        this.syncFirstOnlyFields = this.syncFirstOnlyFields.bind( this );
    }

    createElement() {
        const element = document.createElement( "div" );
        const dialogId = ++nextDialogId;
        this.titleId = `fg-feu-dialog-title-${ dialogId }`;
        this.preparationMessageId = `fg-feu-preparation-message-${ dialogId }`;
        element.classList.add( "fg-feu-dialog" );
        element.setAttribute( "role", "dialog" );
        element.setAttribute( "aria-modal", "true" );
        element.setAttribute( "aria-labelledby", this.titleId );
        element.innerHTML = `<article class="fg-feu-dialog-inner">
    <header class="fg-feu-dialog-header">
        <h2 class="fg-feu-dialog-title"></h2>
        <button class="fg-feu-header-button fg-feu-dialog-close"></button>
    </header>
    <div class="fg-feu-dialog-body"></div>
    <footer class="fg-feu-dialog-footer">
        <button type="submit" class="fg-feu-button fg-feu-primary-button fg-feu-dialog-upload" name="foogallery_image_upload"></button>
        <button type="button" class="fg-feu-button fg-feu-dialog-cancel"></button>
    </footer>
</article>`;
        const title = element.querySelector( ".fg-feu-dialog-title" );
        title.id = this.titleId;
        title.textContent = this.parentForm.i18n.dialogTitle;
        const closeButton = element.querySelector( ".fg-feu-dialog-close" );
        closeButton.setAttribute( "aria-label", this.parentForm.i18n.dialogClose );
        closeButton.innerHTML = icons.cross;
        element.querySelector( ".fg-feu-dialog-upload" ).textContent = this.parentForm.i18n.dialogUpload;
        element.querySelector( ".fg-feu-dialog-cancel" ).textContent = this.parentForm.i18n.dialogCancel;
        return element;
    }

    createResultNotice( result ) {
        const partial = result.errored > 0 || result.skipped > 0;
        let icon, className;
        if ( result.success === true ) {
            icon = partial ? icons.warn : icons.success;
            className = partial ? "fg-feu-warn" : "fg-feu-success";
        } else {
            icon = icons.error;
            className = "fg-feu-error";
        }
        const element = this.createNotice( result.message, className, icon );
        if ( partial ) {
            const details = this.createResultDetails( result );
            element.append( details );
        }
        return element;
    }

    createResultDetails( result ) {
        const details = document.createElement( "div" );
        details.classList.add( "fg-feu-result-details" );
        const successes = result.files.filter( file => file.status === "succeeded" );
        if ( successes.length > 0 ) {
            details.append( this.createResultFilesList( successes, this.parentForm.i18n.resultSucceeded, "fg-feu-succeeded" ) );
        }
        const errors = result.files.filter( file => file.status === "errored" );
        if ( errors.length > 0 ) {
            details.append( this.createResultFilesList( errors, this.parentForm.i18n.resultErrored, "fg-feu-errored" ) );
        }
        const skips = result.files.filter( file => file.status === "skipped" );
        if ( skips.length > 0 ) {
            details.append( this.createResultFilesList( skips, this.parentForm.i18n.resultSkipped, "fg-feu-skipped" ) );
        }
        return details;
    }

    createResultFilesList( files, title, className, icon = "" ) {
        const element = document.createElement( "div" );
        element.classList.add( "fg-feu-result-files", className );

        const header = document.createElement( "span" );
        header.classList.add( "fg-feu-result-files-header" );
        if ( typeof icon === "string" && icon.length > 0 ) {
            header.innerHTML = icon;
            header.append( document.createTextNode( title ) );
        } else {
            header.textContent = title;
        }
        element.append( header );

        const list = document.createElement( "ul" );
        list.classList.add( "fg-feu-result-files-list" );
        for ( const file of files ) {
            const li = document.createElement( "li" );
            li.textContent = `${ file.name }` + ( file.message.length > 0 ? ` - ${ file.message }` : "" );
            list.append( li );
        }
        element.append( list );

        return element;
    }

    mount() {
        if ( this.element.isConnected ) return false;
        document.documentElement.classList.add( "fg-feu-dialog-open" );
        this.parentForm.element.append( this.element );
        this.trapFocus();
        return true;
    }

    clearSelectionState() {
        this.isPreparing = false;
        this.preparationMessage = "";
        this.preparationTotal = 0;
        this.element.classList.remove( "fg-feu-validation-attempted" );
        this.empty();
        this._firstOnlyValues.clear();
        this.buttonUpload.classList.remove( "fg-feu-hidden" );
        this.buttonUpload.disabled = true;
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
        this.buttonCancel.textContent = this.parentForm.i18n.dialogCancel;
    }

    open() {
        this.isPreparing = false;
        this.checkHasFields();
        this.checkIsLast();
        this.populate();
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
        const mounted = this.mount();
        if ( mounted ) this.setInitialFocus();
    }

    formatPreparationMessage( completed, total ) {
        const current = String( completed );
        const maximum = String( total );
        return String( this.preparationMessage ?? "" )
            .replace( /%1\$d/g, current )
            .replace( /%2\$d/g, maximum )
            .replace( "%d", current )
            .replace( "%d", maximum );
    }

    openPreparation( message, total ) {
        this.clearSelectionState();
        this.checkHasFields();
        this.isPreparing = true;
        this.preparationMessage = message;
        this.preparationTotal = Math.max( 0, Number( total ) || 0 );

        const status = document.createElement( "div" );
        status.classList.add( "fg-feu-preparation", "fg-feu-preparation-status" );
        status.setAttribute( "role", "status" );
        status.setAttribute( "aria-live", "polite" );
        status.setAttribute( "aria-atomic", "true" );

        const text = document.createElement( "span" );
        text.classList.add( "fg-feu-preparation-message" );
        text.id = this.preparationMessageId;
        const progress = document.createElement( "progress" );
        progress.classList.add( "fg-feu-preparation-progress" );
        progress.setAttribute( "aria-labelledby", this.preparationMessageId );
        progress.setAttribute( "aria-valuemin", "0" );
        progress.max = Math.max( 1, this.preparationTotal );
        progress.value = 0;
        status.append( text, progress );
        this.body.replaceChildren( status );
        this.updatePreparationProgress( 0, this.preparationTotal );

        const mounted = this.mount();
        if ( mounted ) this.setInitialFocus();
    }

    updatePreparationProgress( completed, total = this.preparationTotal ) {
        if ( !this.isPreparing ) return;
        this.preparationTotal = Math.max( 0, Number( total ) || 0 );
        const current = Math.min( this.preparationTotal, Math.max( 0, Number( completed ) || 0 ) );
        const message = this.formatPreparationMessage( current, this.preparationTotal );
        const text = this.body.querySelector( ".fg-feu-preparation-message" );
        const progress = this.body.querySelector( ".fg-feu-preparation-progress" );
        if ( text ) text.textContent = message;
        if ( progress ) {
            progress.max = Math.max( 1, this.preparationTotal );
            progress.value = current;
            progress.setAttribute( "aria-valuemax", String( this.preparationTotal ) );
            progress.setAttribute( "aria-valuenow", String( current ) );
            progress.setAttribute( "aria-valuetext", message );
        }
    }

    showPreparedItems() {
        if ( !this.element.isConnected ) return;
        this.isPreparing = false;
        this.checkHasFields();
        this.populate();
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
    }

    showPreparationError( message ) {
        if ( !this.element.isConnected ) return;
        this.isPreparing = false;
        this.empty();
        const notice = this.createNotice( message, [ "fg-feu-error", "fg-feu-preparation-error" ], icons.error );
        notice.setAttribute( "role", "alert" );
        this.body.replaceChildren( notice );
        this.buttonUpload.disabled = true;
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
    }

    close() {
        this.isPreparing = false;
        this.preparationMessage = "";
        this.preparationTotal = 0;
        this.buttonUpload.classList.remove( "fg-feu-hidden" );
        this.buttonUpload.disabled = false;
        this.buttonCancel.textContent = this.parentForm.i18n.dialogCancel;
        this.element.classList.remove( "fg-feu-validation-attempted" );
        this.empty();
        this._firstOnlyValues.clear();
        this.parentForm.reset();
        this.releaseFocus();
        document.documentElement.classList.remove( "fg-feu-dialog-open" );
        this.element.remove();
    }

    syncFirstOnlyFields( field, value ) {
        this._firstOnlyValues.set( field, value );
        const fileInfos = Array.from( this.fileInfos.values() );
        fileInfos.forEach( fileInfo => {
            const input = fileInfo.element.querySelector( `[data-field="${ field }"]` );
            if ( input ) input.value = value;
        } );
    }

    captureFieldValues() {
        const values = new Map();
        this.fileInfos.forEach( fileInfo => {
            const itemValues = new Map();
            fileInfo.element.querySelectorAll( "[data-field]" ).forEach( input => {
                if ( input.dataset.firstOnly === "yes" ) {
                    if ( fileInfo.isFirstOnlyOwner ) {
                        this._firstOnlyValues.set( input.dataset.field, input.value );
                    }
                    return;
                }
                itemValues.set( input.dataset.field, input.value );
            } );
            values.set( fileInfo.uploadItem, itemValues );
        } );
        return values;
    }

    populate() {
        const fieldValues = this.captureFieldValues();
        this.empty();

        const [ passedSize, tooLarge ] = splitItems(
            this.parentForm.uploadItems,
            item => item.optimizationError === true || this.parentForm.maxSize === 0 || item.file.size < this.parentForm.maxSize
        );
        const [ passedTypes, wrongTypes ] = splitItems(
            passedSize,
            item => item.optimizationError === true || this.parentForm.isSupportedFile( item.file )
        );
        const [ files, tooMany ] = splitUploadItemsByLimit( passedTypes, this.parentForm.maxImages );

        const notice = this.createOpenNotice( files, tooLarge, tooMany, wrongTypes );
        if ( notice instanceof HTMLElement ) this.body.append( notice );

        this.parentForm.setUploadItems( files );
        this.checkFilesChanged();

        for ( const item of this.parentForm.uploadItems ) {
            const fi = new FileInfo( this, item );
            const itemValues = fieldValues.get( item );
            if ( itemValues ) {
                itemValues.forEach( ( value, field ) => {
                    const input = fi.element.querySelector( `[data-field="${ field }"]` );
                    if ( input ) input.value = value;
                } );
            }
            this.body.append( fi.element );
            this.fileInfos.set( fi.element, fi );
        }
    }

    createNotice( message, className, icon = "", sticky = false ) {
        const notice = document.createElement( "div" );
        notice.classList.add( "fg-feu-notice" );
        notice.classList.toggle( "fg-feu-notice-sticky", sticky );
        if ( typeof className === "string" && className.length > 0 ) {
            notice.classList.add( className );
        }
        if ( Array.isArray( className ) && className.length > 0 ) {
            notice.classList.add( ...className );
        }
        const msg = document.createElement( "span" );
        msg.classList.add( "fg-feu-message" );
        if ( typeof icon === "string" && icon.length > 0 ) {
            msg.innerHTML = icon;
            msg.append( document.createTextNode( message ) );
        } else {
            msg.textContent = message;
        }
        notice.append( msg );
        return notice;
    }

    createOpenNotice( files, tooLarge, tooMany, wrongTypes ) {
        if ( tooLarge.length > 0 || tooMany.length > 0 || wrongTypes.length > 0 ) {
            const messages = [];
            if ( tooLarge.length > 0 ) {
                let message;
                if ( files.length === 0 ) {
                    message = tooLarge.length === 1 ? this.parentForm.i18n.fileAllTooLarge : this.parentForm.i18n.fileAllTooLargePlural;
                } else {
                    message = tooLarge.length === 1 ? this.parentForm.i18n.fileSomeTooLarge : this.parentForm.i18n.fileSomeTooLargePlural;
                }
                messages.push( message.replace( "%s", formatBytes( this.parentForm.maxSize ) ) );
            }
            if ( tooMany.length > 0 ) {
                const message = this.parentForm.maxImages === 1 ? this.parentForm.i18n.fileTooMany : this.parentForm.i18n.fileTooManyPlural;
                messages.push( message.replace( "%d", String( this.parentForm.maxImages ) ) );
            }
            if ( wrongTypes.length > 0 ) {
                let message;
                if ( wrongTypes.length === 1 ) {
                    const extension = wrongTypes[0].file.name.split( "." ).pop()?.toLowerCase();
                    message = this.parentForm.i18n.fileTypeNotAllowed.replace( "%s", extension );
                } else {
                    message = this.parentForm.i18n.fileTypeNotAllowedPlural;
                }
                const allowedLabel = this.parentForm.allowedTypes.join( ", " ) || ( this.parentForm.videoUploadsEnabled ? "image files, mp4" : "image files" );
                message = message.replace( "%s", allowedLabel );
                messages.push( message );
            }
            if ( messages.length > 0 ) {
                return this.createNotice( messages.join( " " ), "fg-feu-warn", icons.warn, files.length > 0 );
            }
        }
        return null;
    }

    empty() {
        for ( const fi of this.fileInfos.values() ) {
            fi.remove();
        }
        this.fileInfos.clear();
        this.body.replaceChildren();
    }

    showLoader() {
        if ( !( this.loader instanceof HTMLDivElement ) ) {
            this.loader = this.createNotice( this.parentForm.i18n.restUploading, [ "fg-feu-info", "fg-feu-loader" ], icons.loader );
        }
        this.body.replaceChildren( this.loader );
        this.buttonUpload.disabled = true;
        this.buttonCancel.disabled = true;
        this.buttonClose.disabled = true;
    }

    hideLoader() {
        this.populate();
        this.buttonUpload.disabled = false;
        this.buttonCancel.disabled = false;
        this.buttonClose.disabled = false;
    }

    showResult( result ) {
        const element = this.createResultNotice( result );
        this.body.replaceChildren( element );
        this.buttonUpload.classList.add( "fg-feu-hidden" );
        this.buttonClose.disabled = false;
        this.buttonCancel.disabled = false;
        this.buttonCancel.textContent = this.parentForm.i18n.dialogClose;
    }

    buildFormData( validInfos ) {
        const formData = new FormData();
        this.parentForm.element.querySelectorAll( "input[type='hidden']" ).forEach( input => {
            if ( input.name ) {
                formData.append( input.name, input.value );
            }
        } );

        validInfos.forEach( ( fileInfo, index ) => {
            fileInfo.appendToFormData( formData, index, index === 0 );
        } );

        return formData;
    }

    buildLocalSkippedResults() {
        return Array.from( this.fileInfos.values() )
            .filter( fileInfo => fileInfo.hasBlockingError )
            .map( fileInfo => ( {
                status: "skipped",
                name: fileInfo.file.name,
                message: fileInfo.getBlockingError()
            } ) );
    }

    mergeLocalSkippedResults( result, localSkipped ) {
        if ( !Array.isArray( localSkipped ) || localSkipped.length === 0 ) {
            return result;
        }

        const merged = Object.assign(
            { files: [], succeeded: 0, errored: 0, skipped: 0, success: false, message: "" },
            result
        );

        merged.files = Array.isArray( merged.files ) ? merged.files.slice() : [];
        merged.files.push( ...localSkipped );
        merged.skipped += localSkipped.length;
        if ( typeof merged.message === "string" ) {
            const skipMessage = localSkipped.length === 1
                ? this.parentForm.i18n.resultLocalSkippedSingle
                : this.parentForm.i18n.resultLocalSkippedPlural;
            merged.message += skipMessage.replace( "%d", String( localSkipped.length ) );
        }
        return merged;
    }

    checkFilesChanged() {
        this.checkIsLast();
        this.buttonUpload.disabled = !this.parentForm.hasValidUploadItems;
    }

    checkIsLast() {
        this.element.classList.toggle( "fg-feu-single", this.parentForm.hasSingleFile );
    }

    checkHasFields() {
        this.element.classList.toggle( "fg-feu-no-fields", !this.parentForm.hasFields );
    }

    onCancelOrCloseClicked( event ) {
        event.preventDefault();
        this.close();
    }

    async onUploadClicked( event ) {
        event.preventDefault();
        this.element.classList.add( "fg-feu-validation-attempted" );

        const validInfos = Array.from( this.fileInfos.values() ).filter( fileInfo => !fileInfo.hasBlockingError );
        if ( validInfos.length === 0 ) {
            const localSkipped = this.buildLocalSkippedResults();
            const message = localSkipped[0]?.message || this.parentForm.i18n.noValidFiles;
            this.showValidationError( message );
            return;
        }

        const validationResult = this.validateRequiredFields( validInfos );
        if ( !validationResult.valid ) {
            this.showValidationError( validationResult.message );
            return;
        }

        const formData = this.buildFormData( validInfos );
        const localSkipped = this.buildLocalSkippedResults();
        this.showLoader();
        const result = await this.parentForm.upload( formData );
        const mergedResult = this.mergeLocalSkippedResults( result, localSkipped );

        if ( mergedResult.errored > 0 && mergedResult.succeeded === 0 ) {
            const hasValidationError = Array.isArray( mergedResult.files ) && mergedResult.files.some( file =>
                file.status === "errored" &&
                file.message &&
                file.message.includes( "Required field" )
            );

            if ( hasValidationError ) {
                this.hideLoader();
                const errorMessages = mergedResult.files
                    .filter( file => file.status === "errored" )
                    .map( file => file.message )
                    .join( ", " );
                this.showValidationError( errorMessages );
                return;
            }
        }

        this.showResult( mergedResult );
    }

    validateRequiredFields( validInfos ) {
        if ( this.parentForm.requiredFields.length === 0 ) {
            return { valid: true, message: "" };
        }

        const invalidFields = [];
        validInfos.forEach( fileInfo => {
            const requiredInputs = fileInfo.element.querySelectorAll( "input[required], textarea[required], select[required]" );
            requiredInputs.forEach( input => {
                if ( !input.checkValidity() ) {
                    const label = input.closest( ".fg-feu-file-field" );
                    const fieldName = label?.querySelector( ".fg-feu-file-field-name" )?.textContent?.replace( " *", "" ) || input.name;
                    invalidFields.push( fieldName );
                }
            } );
        } );

        if ( invalidFields.length > 0 ) {
            return { valid: false, message: this.parentForm.i18n.requiredFieldsError || "Please fill in all required fields (marked with *)." };
        }

        return { valid: true, message: "" };
    }

    showValidationError( message ) {
        const existingNotice = this.body.querySelector( ".fg-feu-validation-error" );
        if ( existingNotice ) {
            existingNotice.remove();
        }

        const notice = this.createNotice( message, [ "fg-feu-error", "fg-feu-validation-error" ], icons.error, true );
        this.body.insertBefore( notice, this.body.firstChild );
        this.body.scrollTop = 0;

        const firstInvalid = this.element.querySelector( "input[required]:invalid, textarea[required]:invalid, select[required]:invalid" );
        if ( firstInvalid instanceof HTMLElement ) {
            firstInvalid.focus();
        }
    }

    setInitialFocus() {
        if ( this.isPreparing ) {
            this.buttonCancel.focus();
        } else if ( this.parentForm.hasFields ) {
            const first = this.body.querySelector( ".fg-feu-file-field-input" );
            if ( first instanceof HTMLElement ) first.focus();
        } else {
            this.buttonUpload.focus();
        }
    }

    #activeElementReset = null;

    trapFocus() {
        if ( !( this.#activeElementReset instanceof HTMLElement ) || !this.#activeElementReset.isConnected ) {
            this.#activeElementReset = document.activeElement ?? null;
        }
        this.element.addEventListener( "keydown", this.onTrapFocusKeyDown );
    }

    releaseFocus() {
        this.element.removeEventListener( "keydown", this.onTrapFocusKeyDown );
        if ( this.#activeElementReset instanceof HTMLElement ){
            this.#activeElementReset.focus();
            this.#activeElementReset = null;
        }
    }

    onTrapFocusKeyDown( event ) {
        if ( event.key !== "Tab" ) return;

        const focusable = Array.from( this.element.querySelectorAll( "button, input, textarea, select, [href]" ) )
            .filter( element => !element.hasAttribute( "disabled" ) && !element.getAttribute( "aria-hidden" ) );
        if ( focusable.length === 0 ) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if ( event.shiftKey && document.activeElement === first ) {
            event.preventDefault();
            last.focus();
        } else if ( !event.shiftKey && document.activeElement === last ) {
            event.preventDefault();
            first.focus();
        }
    }
};
